/**
 * Thresholds for adaptive x-axis label behavior.
 */
const SHORT_LABEL_MAX_LENGTH = 10;
const MEDIUM_LABEL_MAX_LENGTH = 20;
const SMALL_BAR_COUNT_THRESHOLD = 5;
const NORMAL_BAR_COUNT_THRESHOLD = 8;
const TRUNCATION_LENGTH = 15;
const TRUNCATION_SUFFIX = '...';

const ROTATION_ANGLE_MODERATE = -30;
const ROTATION_ANGLE_STEEP = -45;

const BOTTOM_MARGIN_NORMAL = 60;
const BOTTOM_MARGIN_ROTATED = 80;
const BOTTOM_MARGIN_TRUNCATED = 100;

type LabelTreatment = 'normal' | 'rotated' | 'truncated';

type LabelStrategy = {
  treatment: LabelTreatment;
  angle: number;
  textAnchor: 'start' | 'middle' | 'end';
  truncateAt: number | null;
  bottomMargin: number;
};

/**
 * Analyze chart data labels and return a strategy for rendering them.
 *
 * @param labels - Array of x-axis label strings
 * @returns LabelStrategy describing how to render the labels
 */
function computeLabelStrategy(labels: string[]): LabelStrategy {
  if (labels.length === 0) {
    return {
      treatment: 'normal',
      angle: 0,
      textAnchor: 'middle',
      truncateAt: null,
      bottomMargin: BOTTOM_MARGIN_NORMAL,
    };
  }

  const maxLen = Math.max(...labels.map((l) => l.length));
  const barCount = labels.length;

  // Short labels with few bars: no treatment needed
  if (maxLen <= SHORT_LABEL_MAX_LENGTH && barCount <= NORMAL_BAR_COUNT_THRESHOLD) {
    return {
      treatment: 'normal',
      angle: 0,
      textAnchor: 'middle',
      truncateAt: null,
      bottomMargin: BOTTOM_MARGIN_NORMAL,
    };
  }

  // Medium labels or moderate bar count: rotate slightly
  if (maxLen <= MEDIUM_LABEL_MAX_LENGTH || barCount <= SMALL_BAR_COUNT_THRESHOLD) {
    return {
      treatment: 'rotated',
      angle: ROTATION_ANGLE_MODERATE,
      textAnchor: 'end',
      truncateAt: null,
      bottomMargin: BOTTOM_MARGIN_ROTATED,
    };
  }

  // Long labels or many bars: truncate and rotate steeply
  return {
    treatment: 'truncated',
    angle: ROTATION_ANGLE_STEEP,
    textAnchor: 'end',
    truncateAt: TRUNCATION_LENGTH,
    bottomMargin: BOTTOM_MARGIN_TRUNCATED,
  };
}

export {
  computeLabelStrategy,
  // Constants exported for tests
  SHORT_LABEL_MAX_LENGTH,
  MEDIUM_LABEL_MAX_LENGTH,
  SMALL_BAR_COUNT_THRESHOLD,
  NORMAL_BAR_COUNT_THRESHOLD,
  TRUNCATION_LENGTH,
  TRUNCATION_SUFFIX,
  ROTATION_ANGLE_MODERATE,
  ROTATION_ANGLE_STEEP,
  BOTTOM_MARGIN_NORMAL,
  BOTTOM_MARGIN_ROTATED,
  BOTTOM_MARGIN_TRUNCATED,
};
export type { LabelStrategy, LabelTreatment };
